import { useCallback, useEffect, useState } from 'react'

import { mapGraphQLOrderToInternal } from 'lib/utils/orderUtils'

import { OrderSearchInputType, useSearchUserOrdersApi } from '@/api/useSearchOrderApi'
import { useCreateSmsTracking, useGetSmsTracking } from '@/api/useSmsTrackingApi'
import useSessionStore, { SessionStore } from '@/stores/useSessionStore'
import { brand } from '@/utils/brand'
// Move the selector outside to ensure it's always the same reference
const selectAuthToken = (store: SessionStore): string | null => store.authToken

interface TrackingSearchData {
  searchParam: string
  page?: number
  pageSize?: number
}
const DEFAULT_PAGE_SIZE = 5

const defaultSearchQuery: OrderSearchInputType = {
  searchParam: '',
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
}

interface UseSearchOrdersResult {
  searchOrdersLoading: boolean
  searchError: string | undefined
  orders: Order[] | null
  user?: OrderUser
  totalOrders: number
  currentPage: number
  pageSize: number
  handleOrderSearch: (data: TrackingSearchData) => void
  handlePageChange: (page: number, searchTerm?: string) => void
  isLoggedIn: boolean
  isSmsSubscribed?: boolean
  updateSMSSubscription?: (phone: string, terms: boolean) => void
  smsOptInError?: string
  isCreatingSmsTracking?: boolean
  createdSmsTracking?: boolean
}

export const useSearchOrders = (): UseSearchOrdersResult => {
  const [searchError, setSearchError] = useState<string | null>(null)
  const [smsOptInError, setSmsOptInError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState<OrderSearchInputType>(defaultSearchQuery)
  const [orders, setOrders] = useState<Order[]>([])
  const [hasInitialLoad, setHasInitialLoad] = useState(false)
  const [isSmsSubscribed, setIsSmsSubscribed] = useState<boolean>(false)

  const authToken = useSessionStore(selectAuthToken)
  const { data: smsTrackingData, refetch: refetchSmsTracking } = useGetSmsTracking(!!authToken)
  const {
    mutate: createSmsTracking,
    isPending: isCreatingSmsTracking,
    isSuccess: createdSmsTracking,
  } = useCreateSmsTracking()

  const {
    data: searchOrdersResult,
    isLoading: searchOrdersLoading,
    error: searchOrdersError,
  } = useSearchUserOrdersApi(searchQuery)

  // Reset state when auth token changes
  useEffect(() => {
    setSearchQuery(defaultSearchQuery)
    setSearchError(null)
    setOrders([])
    setHasInitialLoad(false)
  }, [authToken])

  useEffect(() => {
    if (searchOrdersError) {
      setSearchError('Failed to fetch orders. Please try again.')
    }
  }, [searchOrdersError])

  // Load initial orders — but never clobber an explicit order search (the
  // tracking page sets searchParam from the URL; resetting it to the default
  // empty query disables the API fetch entirely and strands the page).
  useEffect(() => {
    if (!hasInitialLoad && !searchOrdersLoading && !searchQuery.searchParam) {
      setSearchQuery(defaultSearchQuery)
      setHasInitialLoad(true)
    }
  }, [hasInitialLoad, searchOrdersLoading, searchQuery.searchParam])

  useEffect(() => {
    if (smsTrackingData) {
      setIsSmsSubscribed(smsTrackingData.data?.sms_tracking?.optin || false)
    }
  }, [smsTrackingData])

  // Handle search results
  useEffect(() => {
    if (!searchOrdersLoading && searchOrdersResult) {
      try {
        const mappedOrders = searchOrdersResult.orders.map((order: ApiOrder) => {
          return mapGraphQLOrderToInternal(order)
        })
        setOrders(mappedOrders)
        setSearchError(null) // Clear any previous errors
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        setSearchError('Error processing order data')
      }
    }
  }, [authToken, searchOrdersLoading, searchOrdersResult, searchQuery.searchParam])

  // Handle tracking search submission
  const handleOrderSearch = useCallback(
    (data: TrackingSearchData) => {
      if (!data.searchParam || data.searchParam.length === 0) {
        setSearchError('Order number is required')
        return
      }

      setSearchError(null) // Reset error on new search

      setSearchQuery({
        searchParam: data.searchParam || '',
        page: data.page || 1,
        pageSize: data.pageSize || DEFAULT_PAGE_SIZE,
      })
    },
    [setSearchError, setSearchQuery],
  )

  // Handle page change
  const handlePageChange = useCallback((page: number, searchTerm?: string) => {
    setSearchQuery((prev) => ({
      ...prev,
      page,
      searchParam: searchTerm || '',
    }))
  }, [])

  const handleSubscribeSmsTracking = useCallback(
    (phone: string, terms: boolean) => {
      if (!authToken) {
        setSmsOptInError('You must be logged in to subscribe to SMS updates')
        return
      } else if (!phone || phone.length === 0) {
        setSmsOptInError('Phone number is required for SMS updates')
        return
      }

      const cleanedPhone = phone.replace(/\D/g, '')
      const formattedPhone = cleanedPhone.startsWith('1') ? `+${cleanedPhone}` : `+1${cleanedPhone}`

      const payload = {
        brand_id: brand.key,
        phone_number: formattedPhone,
        terms_accepted: terms,
        optin: true,
      }
      createSmsTracking(payload, {
        onSuccess: () => {
          setSmsOptInError(null)
          refetchSmsTracking()
        },
        onError: () => {
          setSmsOptInError('Failed to set SMS subscription. Please try again.')
        },
      })
    },
    [authToken, createSmsTracking, refetchSmsTracking],
  )

  return {
    searchOrdersLoading,
    searchError: searchError || undefined,
    orders,
    totalOrders: searchOrdersResult?.totalCount || 0,
    currentPage: searchQuery.page || 1,
    pageSize: searchQuery.pageSize || DEFAULT_PAGE_SIZE,
    handleOrderSearch,
    handlePageChange,
    isLoggedIn: !!authToken,
    isSmsSubscribed: isSmsSubscribed,
    updateSMSSubscription: handleSubscribeSmsTracking,
    smsOptInError: smsOptInError || undefined,
    isCreatingSmsTracking: isCreatingSmsTracking,
    createdSmsTracking: createdSmsTracking || false,
    user: orders?.[0]?.user,
  }
}
